import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  AuthProvider,
  CurrencyProvider,
  SnackbarProvider,
  ErrorBoundary,
  debugInit,
  sentryInit,
} from '@resort-os/ui';
import { CartProvider } from './contexts/CartContext';
import App from './App';
import '@resortifysolutions/theme/styles.css';
import '@resortifysolutions/layout/index.css';

const theme = createTheme({
  typography: {
    h1: { fontSize: '2.5rem', fontWeight: 600 },
    h2: { fontSize: '2rem', fontWeight: 600 },
    h3: { fontSize: '1.5rem', fontWeight: 500 },
    h4: { fontSize: '1.25rem', fontWeight: 500 },
    h5: {
      fontSize: '0.9rem',
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    h6: {
      fontSize: '0.8rem',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
    body1: { fontSize: '0.9375rem' },
    body2: { fontSize: '0.8125rem' },
    button: {
      fontWeight: 500,
      letterSpacing: '0.04em',
    },
    caption: {
      fontSize: '0.75rem',
      letterSpacing: '0.04em',
    },
  },
  palette: {
    mode: 'light',
    primary: { main: '#2C3E2D', contrastText: '#fff' },
    secondary: { main: '#6B6560', contrastText: '#fff' },
    background: { default: '#FAFAF8', paper: '#FFFFFF' },
    text: { primary: '#1A1A1A', secondary: '#6B6560' },
    divider: '#E2DDD8',
  },
  shape: { borderRadius: 0 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {},
        body: { backgroundColor: '#FAFAF8' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 0, textTransform: 'none', boxShadow: 'none' },
        contained: { '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2DDD8' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2C3E2D' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2C3E2D' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '0.875rem' },
      },
    },
    MuiSelect: {
      styleOverrides: { root: { borderRadius: 0 } },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none', boxShadow: 'none' } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { border: '1px solid #E2DDD8', borderRadius: 0, boxShadow: 'none' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          borderBottom: '1px solid #E2DDD8',
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: '#6B6560',
          backgroundColor: '#FAFAF8',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: '#FAFAF8' },
          '&:last-child td': { borderBottom: 0 },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 0 } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: 'none' } },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#2C3E2D', height: 2 },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

sentryInit();
debugInit();
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <CurrencyProvider>
            <CartProvider>
              <SnackbarProvider>
                <BrowserRouter>
                  <ErrorBoundary>
                    <App />
                  </ErrorBoundary>
                </BrowserRouter>
              </SnackbarProvider>
            </CartProvider>
          </CurrencyProvider>
        </AuthProvider>
      </MuiThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
